import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { firestored } from "../../firebase/firebaseConfig";
import { Mail, User, Phone, BadgeCheck } from "lucide-react";
import PageHeader from "../PageHeader";

export default function ProfilePage() {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const ref = doc(firestored, "users", id);
        const snap = await getDoc(ref);
        if (snap.exists()) setUser(snap.data());
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id]);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-200">
        <div className="animate-spin h-12 w-12 border-4 border-gray-300 border-t-indigo-600 rounded-full"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="h-screen flex items-center justify-center text-gray-500">
        User not found
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-white to-gray-200">
      <PageHeader
        title={`${user.firstname} ${user.lastname}`}
        subtitle={user.role || "User"}
      />
      <div className="max-w-5xl mx-auto py-10 px-4">

        {/* Main Card */}
        <div className="relative rounded-3xl overflow-hidden shadow-2xl backdrop-blur-xl bg-white/70 border border-white/40">

          {/* Cover */}
          <div className="h-48 bg-gradient-to-r from-indigo-600 via-blue-600 to-purple-600 relative">
            <div className="absolute inset-0 bg-black/10"></div>
          </div>

          {/* Avatar */}
          <div className="absolute top-32 left-10">
            <img
              src={user.profilepicture || "/assets/user.png"}
              alt="profile"
              className="w-32 h-32 rounded-full border-4 border-white object-cover shadow-xl"
            />
            <span className="absolute bottom-3 right-3 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></span>
          </div>

          {/* Content */}
          <div className="pt-20 px-10 pb-10">

            {/* Name + Info */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">

              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-3xl font-bold text-gray-900">
                    {user.firstname} {user.lastname}
                  </h1>
                  <BadgeCheck className="text-indigo-500" />
                </div>

                <p className="text-gray-500 mt-1">
                  @{user.username || "user"}
                </p>

                <p className="text-sm text-gray-400 mt-2">
                  {user.role || "User"}
                </p>
              </div>


            </div>

            {/* Info Section */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10">

              <div className="flex items-center gap-3 p-5 rounded-2xl bg-white/60 border backdrop-blur hover:shadow-md transition">
                <Mail className="text-indigo-600" />
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="font-medium text-gray-800 break-all">
                    {user.email}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-5 rounded-2xl bg-white/60 border backdrop-blur hover:shadow-md transition">
                <User className="text-indigo-600" />
                <div>
                  <p className="text-xs text-gray-500">Role</p>
                  <p className="font-medium text-gray-800">
                    {user.role || "User"}
                  </p>
                </div>
              </div>

              {user.phonenumber && (
                <div className="flex items-center gap-3 p-5 rounded-2xl bg-white/60 border backdrop-blur hover:shadow-md transition">
                  <Phone className="text-indigo-600" />
                  <div>
                    <p className="text-xs text-gray-500">Phone</p>
                    <p className="font-medium text-gray-800">
                      {user.phonenumber}
                    </p>
                  </div>
                </div>
              )}

            </div>

          </div>
        </div>

      </div>
    </div>
  );
}